import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, CreditCard, FloppyDisk, Eye, EyeSlash, CheckCircle, Warning, Spinner, ArrowsClockwise } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { extractErr } from "@/lib/errors";
import { toast } from "sonner";
import AccountMenu from "@/components/AccountMenu";
import { useT } from "@/lib/i18n";

/**
 * PaymentCredentialsPage — super-admin only.
 *
 * Two columns (US Air vs ACC). For each tenant:
 *   • Active-processor radio (Stripe ↔ Elavon)
 *   • Credential fields for both processors (only the active one's are
 *     used at runtime; the other set stays as a configured-but-idle backup)
 *   • Test-connection button live-pings whichever processor is active
 *
 * Mirrors the Twilio Credentials page UX for visual consistency.
 */
export default function PaymentCredentialsPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const tx = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [edits, setEdits] = useState({}); // {tenant: {field: value}}
  const [reveal, setReveal] = useState({}); // {tenantField: bool}
  const [saving, setSaving] = useState({}); // {tenant: bool}
  const [testing, setTesting] = useState({}); // {tenant: bool}
  const [testResult, setTestResult] = useState({}); // {tenant: result}

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get("/admin/payment-credentials");
      setData(r.data);
      setEdits({});
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load payment credentials");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (tenant, name, val) => {
    setEdits((p) => ({ ...p, [tenant]: { ...(p[tenant] || {}), [name]: val } }));
  };

  const saveTenant = async (tenant, processor) => {
    const ed = edits[tenant] || {};
    const payload = { tenant };
    if (processor) payload.processor = processor;
    if (Object.keys(ed).length > 0) payload.fields = ed;
    if (!payload.processor && !payload.fields) return;
    if (!window.confirm(`Save changes for ${tenant === "US" ? "US Air" : "ACC"}?`)) return;
    setSaving((s) => ({ ...s, [tenant]: true }));
    try {
      await api.put("/admin/payment-credentials", payload);
      await load();
      setTestResult((r) => ({ ...r, [tenant]: null }));
    } catch (e) {
      toast.error(`Save failed: ${extractErr(e, "Save failed").message}`);
    } finally {
      setSaving((s) => ({ ...s, [tenant]: false }));
    }
  };

  const testTenant = async (tenant) => {
    setTesting((t) => ({ ...t, [tenant]: true }));
    setTestResult((r) => ({ ...r, [tenant]: null }));
    try {
      const r = await api.post(`/admin/payment-credentials/test/${tenant}`);
      setTestResult((t) => ({ ...t, [tenant]: r.data }));
    } catch (e) {
      setTestResult((t) => ({ ...t, [tenant]: { ok: false, message: e?.response?.data?.detail || e?.message } }));
    } finally {
      setTesting((t) => ({ ...t, [tenant]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500" title={tx("action.back", "Back")}>
            <ArrowLeft size={18} weight="bold" />
          </button>
          <CreditCard size={20} weight="fill" className="text-blue-700" />
          <h1 className="text-base md:text-lg font-black tracking-tighter font-['Chivo']">
            {tx("payment_credentials.title", "Payment Credentials")}
          </h1>
          <button onClick={load} disabled={loading}
            className={`ml-2 w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-100 ${loading ? "text-blue-600" : "text-slate-500"}`}
            title={loading ? "Reloading…" : "Reload"} data-testid="payment-creds-reload-btn">
            <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
          </button>
          <div className="ml-auto"><AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} /></div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-8">
        <div className="mb-4">
          <p className="text-sm text-slate-600">
            {tx("payment_credentials.tenant_info", "Each tenant (US Air & ACC) chooses ONE processor. Mongo overrides take effect")} <strong>{tx("payment_credentials.instantly", "instantly")}</strong> — {tx("payment_credentials.no_restart", "no restart needed.")}
            {tx("payment_credentials.inactive_note", "Inactive processor's fields remain configured but idle until you flip the toggle.")}
          </p>
          <div className="mt-3 text-[11px] font-bold uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm inline-flex items-center gap-1">
            <ShieldCheck size={11} weight="bold" /> Super-admin required
          </div>
        </div>

        {error && <div className="mb-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-3">{error}</div>}

        {loading && !data ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Spinner size={16} className="animate-spin" /> Loading…</div>
        ) : data && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.tenants.map((t) => {
              const ed = edits[t.tenant] || {};
              const dirty = Object.keys(ed).length > 0;
              const tr = testResult[t.tenant];
              return (
                <section key={t.tenant} data-testid={`tenant-${t.tenant}`}
                  className="border border-slate-200 rounded-md bg-white overflow-hidden">
                  <header className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                      Tenant {t.tenant} · {t.currency.toUpperCase()}
                    </div>
                    <div className="text-base font-black tracking-tighter font-['Chivo']">{t.label}</div>
                    {t.last_updated_at && (
                      <div className="text-[10px] text-slate-400 mt-1">
                        {tx("payment_credentials.last_updated_by", "Last updated by")} <span className="font-mono">{t.last_updated_by}</span> {tx("payment_credentials.at_time", "at")} {new Date(t.last_updated_at).toLocaleString()}
                      </div>
                    )}
                  </header>

                  <div className="p-4 border-b border-slate-100">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">{tx("payment_credentials.active_processor", "Active Processor")}</div>
                    <div className="inline-flex items-center rounded-sm border border-slate-200 overflow-hidden">
                      {["stripe", "elavon"].map((p) => {
                        const isActive = (ed.__processor || t.active_processor) === p;
                        return (
                          <button key={p}
                            data-testid={`tenant-${t.tenant}-proc-${p}`}
                            onClick={() => setField(t.tenant, "__processor", p)}
                            className={`h-8 px-4 text-[11px] font-bold uppercase tracking-wider transition-colors ${isActive ? (p === "stripe" ? "bg-blue-600 text-white" : "bg-violet-600 text-white") : "text-slate-500 hover:bg-slate-100"}`}>
                            {p}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {Object.entries(t.fields).map(([proc, fields]) => (
                    <details key={proc} open={(ed.__processor || t.active_processor) === proc}
                      className="border-b last:border-b-0 border-slate-100">
                      <summary className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-slate-600 cursor-pointer hover:bg-slate-50 flex items-center justify-between">
                        <span>{proc} credentials</span>
                        {(ed.__processor || t.active_processor) === proc && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
                        )}
                      </summary>
                      <div className="px-4 pb-3 space-y-3">
                        {fields.map((f) => {
                          const key = `${t.tenant}__${f.name}`;
                          const isRevealed = !!reveal[key];
                          const newVal = ed[f.name];
                          const editing = newVal !== undefined;
                          return (
                            <div key={f.name}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-[12px] font-bold text-slate-900">{f.label}</div>
                                  <div className="text-[10px] font-mono text-slate-400">{f.name}</div>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
                                    f.source === "mongo_override" ? "bg-blue-50 text-blue-700 border-blue-200" :
                                    f.source === "env" ? "bg-slate-100 text-slate-600 border-slate-200" :
                                    "bg-rose-50 text-rose-700 border-rose-200"}`}>
                                    {f.source === "mongo_override" ? "Override" : f.source === "env" ? "From .env" : "Missing"}
                                  </span>
                                  {f.has_value && f.secret && (
                                    <button onClick={() => setReveal((r) => ({ ...r, [key]: !r[key] }))}
                                      className="text-slate-400 hover:text-slate-700"
                                      data-testid={`reveal-${key}`}>
                                      {isRevealed ? <EyeSlash size={12} /> : <Eye size={12} />}
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="text-[10px] text-slate-500 mt-0.5 mb-1">{f.hint}</div>
                              <Input
                                data-testid={`input-${key}`}
                                type={f.secret && !editing && !isRevealed ? "password" : "text"}
                                value={editing ? newVal : (f.has_value ? f.active_masked : "")}
                                onChange={(e) => setField(t.tenant, f.name, e.target.value)}
                                placeholder={f.has_value ? f.active_masked : "(not set)"}
                                className="font-mono text-xs h-8"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  ))}

                  <footer className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center gap-2">
                    <button onClick={() => saveTenant(t.tenant, ed.__processor)}
                      disabled={saving[t.tenant] || !dirty}
                      data-testid={`save-${t.tenant}`}
                      className="inline-flex items-center gap-1.5 h-8 px-3 bg-blue-700 hover:bg-blue-800 text-white text-[11px] font-bold uppercase tracking-wider rounded-sm disabled:opacity-50">
                      {saving[t.tenant] ? <Spinner size={11} className="animate-spin" /> : <FloppyDisk size={11} weight="fill" />}
                      {tx("action.save", "Save")}
                    </button>
                    <button onClick={() => testTenant(t.tenant)} disabled={testing[t.tenant]}
                      data-testid={`test-${t.tenant}`}
                      className="inline-flex items-center gap-1.5 h-8 px-3 border border-slate-300 hover:bg-white text-[11px] font-bold uppercase tracking-wider rounded-sm disabled:opacity-50">
                      {testing[t.tenant] ? <Spinner size={11} className="animate-spin" /> : <CheckCircle size={11} weight="fill" />}
                      {tx("payment_credentials.test_connection", "Test connection")}
                    </button>
                    {tr && (
                      <div className={`text-[11px] font-medium px-2 py-1 rounded-sm border ${tr.ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}
                        data-testid={`test-result-${t.tenant}`}>
                        {tr.ok ? (
                          <span className="inline-flex items-center gap-1"><CheckCircle size={10} weight="fill" /> {tr.processor.toUpperCase()} OK · {tr.business || tr.account_id || tr.token_preview || "connected"}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1"><Warning size={10} weight="fill" /> {tr.message}</span>
                        )}
                      </div>
                    )}
                  </footer>
                </section>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
